import React, { useState } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { Check, ChevronsUpDown, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Profile } from '@/hooks/useProfiles';

interface ProfilesComboboxProps {
  profiles: Profile[];
  value: string | null;
  onValueChange: (value: string | null) => void;
  placeholder?: string;
  showAssignmentPlaceholder?: boolean;
}

const ProfilesCombobox: React.FC<ProfilesComboboxProps> = ({
  profiles,
  value,
  onValueChange,
  placeholder = "Selecteer een gebruiker...",
  showAssignmentPlaceholder = false
}) => {
  const [open, setOpen] = useState(false);

  const selectedProfile = value ? profiles.find(profile => profile.id === value) : null;
  const displayName = selectedProfile?.display_name || selectedProfile?.email || 'Onbekende gebruiker';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          <div className="flex items-center gap-2">
            <User className="h-4 w-4" />
            <span className="truncate">
              {value ? displayName : (showAssignmentPlaceholder && !value ? "Wijs lead toe" : placeholder)}
            </span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0">
        <Command>
          <CommandInput placeholder="Zoek gebruiker..." />
          <CommandEmpty>Geen gebruiker gevonden.</CommandEmpty>
          <CommandList>
            <CommandGroup>
              <CommandItem
                value=""
                onSelect={() => {
                  onValueChange(null);
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    !value ? "opacity-100" : "opacity-0"
                  )}
                />
                Niet toegewezen
              </CommandItem>
              {Array.isArray(profiles) && profiles.map((profile) => (
              <CommandItem
                key={profile.id}
                value={String(profile.display_name || profile.email || `user-${profile.id}`)}
                onSelect={() => {
                  onValueChange(profile.id);
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    value === profile.id ? "opacity-100" : "opacity-0"
                  )}
                />
                <div className="flex flex-col">
                  <span>{profile.display_name || profile.email || 'Onbekende gebruiker'}</span>
                  {profile.display_name && profile.email && (
                    <span className="text-xs text-muted-foreground">{profile.email}</span>
                  )}
                </div>
              </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default ProfilesCombobox;